import { ref, computed, watch } from "vue";
import { EventParticipant } from "@/models/EventParticipant";
import { PokerRunEvent } from "@/models/PokerRunEvent";
import { EventUserWaiver } from "@/models/EventUserWaiver";
import EventUserWaiverDataService from "@/services/EventUserWaiverDataService";
import EventParticipantDataService from "@/services/EventParticipantDataService";
import { LeaderboardItem } from "@/models/LeaderboardItem";
import { Unsubscribe } from "firebase/firestore";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";

export default function useObservableEventParticipant(pokerRunEvent: PokerRunEvent, userId: string) {
  // Refs for data
  const participants = ref<EventParticipant[]>([]);
  const userWaiver = ref<EventUserWaiver | null>(null);
  const leaderboard = ref<LeaderboardItem[]>([]);
  const loading = ref(true);
  const error = ref<string | null>(null);

  // Subscription cleanup
  let unsubscribeParticipants: Unsubscribe | null = null;
  let unsubscribeLeaderboard: Unsubscribe | null = null;
  let unsubscribeParticipantsWatch: (() => void) | null = null;

  // Subscribe to participants
  const subscribeToParticipants = async () => {
    unsubscribeParticipants = await EventParticipantDataService.getSubscription(
      pokerRunEvent.id,
      userId,
      (newParticipant) => {
        // Find existing participant index
        const index = participants.value.findIndex((p) => p.id === newParticipant.id);

        if (index !== -1) {
          // Update existing participant
          participants.value = [
            ...participants.value.slice(0, index),
            newParticipant,
            ...participants.value.slice(index + 1),
          ];
        } else {
          // Add new participant
          participants.value = [...participants.value, newParticipant];
        }
      }
    );
  };

  // Subscribe to leaderboard
  const subscribeToLeaderboard = async () => {
    if (!participants.value || participants.value.length === 0) return;

    const participantIds = participants.value.map((p) => p.id as string);
    if (participantIds.length > 0) {
      const { unsubscribe } = await PokerRunEventDataService.getLeaderboard(
        pokerRunEvent.id,
        participantIds,
        (newLeaderboard) => {
          leaderboard.value = newLeaderboard.positions;
        }
      );
      unsubscribeLeaderboard = unsubscribe;
    }
  };

  // Computed properties
  const bestRank = computed(() => {
    if (!participants.value?.length || !leaderboard.value?.length) return null;

    // Get all leaderboard entries for this user's participants
    const userLeaderboardEntries = leaderboard.value.filter((entry) =>
      participants.value.some((p) => p.id === entry.participantId)
    );

    // Sort by rank and return the best (lowest) ranking hand
    return (
      userLeaderboardEntries.sort(
        (a, b) => (a.rank?.rank || Infinity) - (b.rank?.rank || Infinity)
      )[0] || null
    );
  });

  const bestParticipant = computed(() => {
    if (!participants.value?.length || !bestRank.value) return null;

    // Find the participant that matches the best rank
    return participants.value.find((p) => p.id === bestRank.value.participantId);
  });

  const hasAcceptedWaiver = computed(() => {
    return userWaiver.value !== null && participants.value.every((p) => p.acceptedWaiver === true);
  });

  const userWaiverRequired = computed(() => {
    // Check if the event has a waiver requirement
    const event = pokerRunEvent;
    if (!event?.waiver) {
      return false;
    }

    // Check if user has accepted the waiver
    if (userWaiver.value?.acceptedOn) {
      return false;
    }

    // Check if all participants have accepted the waiver
    return participants.value.some(p => !p.acceptedWaiver);
  });

  // Initialize all subscriptions and data loading
  const initialize = async () => {
    try {
      loading.value = true;
      error.value = null;

      // Load waiver data
      userWaiver.value = await EventUserWaiverDataService.getWaiver(pokerRunEvent.id, userId);

      // Set up subscriptions
      await subscribeToParticipants();
      await setTimeout(() => {}, 500);
      // Watch for participant changes to update leaderboard subscription
      unsubscribeParticipantsWatch = watch(
        participants,
        async () => {
          if (unsubscribeLeaderboard) {
            unsubscribeLeaderboard();
          }
          await subscribeToLeaderboard();
        },
        { immediate: true }
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : "An error occurred";
      console.error("Error in useObservableEventParticipant:", err);
    } finally {
      loading.value = false;
    }
  };

  // Cleanup function
  const cleanup = () => {
    if (unsubscribeParticipants) {
      unsubscribeParticipants();
      unsubscribeParticipants = null;
    }
    if (unsubscribeLeaderboard) {
      unsubscribeLeaderboard();
      unsubscribeLeaderboard = null;
    }
    if (unsubscribeParticipantsWatch) {
      unsubscribeParticipantsWatch();
      unsubscribeParticipantsWatch = null;
    }
  };

  // Initialize immediately
  initialize();

  return {
    // Data
    participants,
    userWaiver,
    leaderboard,
    loading,
    error,

    // Computed
    bestRank,
    bestParticipant,
    hasAcceptedWaiver,
    userWaiverRequired,

    // Methods
    initialize,
    cleanup,
  };
}

